import React from "react";
import { motion } from "framer-motion";
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from "recharts";
import { thresholdStatus, statusHex } from "../../data/statusColors";
import { useTranslate } from "../../i18n/useTranslate";

// Same thresholds as before, just resolving through the shared palette:
// default (higher = better): 85/70/50 -> good/info/warning, else critical.
const DEFAULT_BANDS = [
  { min: 85, bucket: "good" },
  { min: 70, bucket: "info" },
  { min: 50, bucket: "warning" },
  { min: -Infinity, bucket: "critical" },
];
// inverse (lower = better): <=10 good, <=25 warning, else critical.
// thresholdStatus checks value >= min (descending), so this is evaluated
// against -value; negate the original boundaries to match (val<=10 <=>
// -val>=-10, val<=25 <=> -val>=-25).
const INVERSE_BANDS = [
  { min: -10, bucket: "good" },
  { min: -25, bucket: "warning" },
  { min: -Infinity, bucket: "critical" },
];

const HEIGHT_BY_SIZE = { sm: 80, md: 100, lg: 130 };

export default function GaugeCard({ title, value, unit="%", inverse=false, subtitle, index=0, size="md" }) {
  const t = useTranslate();
  const color = inverse
    ? statusHex(thresholdStatus(-value, INVERSE_BANDS))
    : statusHex(thresholdStatus(value, DEFAULT_BANDS));
  const data  = [{ value }];
  const h     = HEIGHT_BY_SIZE[size] ?? HEIGHT_BY_SIZE.md;

  return (
    <motion.div
      initial={{ opacity:0, scale:0.9 }}
      animate={{ opacity:1, scale:1 }}
      transition={{ delay: index * 0.08 }}
      className="card p-4 flex flex-col items-center"
    >
      <div className="w-full" style={{ height: h }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart cx="50%" cy="82%" innerRadius="58%" outerRadius="100%"
            barSize={10} data={data} startAngle={180} endAngle={0}>
            <PolarAngleAxis type="number" domain={[0,100]} angleAxisId={0} tick={false} />
            <RadialBar background={{ fill:"#F1F5F9" }} dataKey="value"
              angleAxisId={0} fill={color} cornerRadius={5} />
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
      <div className="text-center -mt-2">
        <div className="text-xl font-black" style={{ color }}>{value}{unit}</div>
        <div className="text-xs font-bold text-slate-700 mt-0.5">{t(title)}</div>
        {subtitle && <div className="text-[10px] text-slate-400 mt-0.5">{t(subtitle)}</div>}
      </div>
    </motion.div>
  );
}
